import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
{
    /* eslint-disable @typescript-eslint/no-unused-vars */
}
import { Box, Divider, IconButton, InputBase, Paper, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import SendIcon from '@material-ui/icons/SendOutlined';
import { useEffect, useRef, useState } from 'react';
import ChatAttachments from './ChatAttachments';
{
    /* Syncfusion editor for future iterations of this component will be commented out for the upcoming release. Code is kept because it works with the current version of the component */
    /* Syncfucion editor imports
    import '@syncfusion/ej2-base/styles/material.css';
    import '@syncfusion/ej2-buttons/styles/material.css';
    import '@syncfusion/ej2-dropdowns/styles/material.css';
    import '@syncfusion/ej2-inputs/styles/material.css';
    import '@syncfusion/ej2-lists/styles/material.css';
    import '@syncfusion/ej2-navigations/styles/material.css';
    import '@syncfusion/ej2-popups/styles/material.css';
    import {
      Inject,
      RichTextEditorComponent,
      HtmlEditor as RteHtmlEditor,
      Image as RteImage,
      Link as RteLink,
      QuickToolbar as RteQuickToolbar,
      Toolbar as RteToolbar,
      ToolbarSettingsModel,
    } from '@syncfusion/ej2-react-richtexteditor';
    import '@syncfusion/ej2-react-richtexteditor/styles/material.css';
    import '@syncfusion/ej2-richtexteditor/styles/material.css';
    import '@syncfusion/ej2-splitbuttons/styles/material.css';
    import TextFormatIcon from '@material-ui/icons/TextFormat';
  */
}
// Create styles
const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
    },
    inputContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.divider}`,
    },
    inputFieldContainer: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'flex-end',
        position: 'relative',
    },
    actionsContainer: {
        display: 'flex',
        position: 'absolute',
        right: 0,
        bottom: 0,
    },
    input: {
        flex: 1,
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        overflow: 'auto',
        maxHeight: '200px',
        minHeight: '40px',
        width: '100%',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[300],
            borderRadius: '4px',
        },
    },
    editorContainer: {
        marginBottom: theme.spacing(1),
        borderRadius: theme.shape.borderRadius,
        width: '100%',
        '& .e-rte-container': {
            border: 0,
        },
        '& .e-richtexteditor .e-toolbar-wrapper': {
            borderTopLeftRadius: theme.shape.borderRadius,
            borderTopRightRadius: theme.shape.borderRadius,
        },
    },
    toolbarButton: {
        padding: 8,
        color: theme.palette.text.secondary,
    },
    fileInput: {
        display: 'none',
    },
    divider: {
        height: 40,
        margin: `0px 4px`,
    },
}));
const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/png',
    'image/jpeg',
    'image/gif',
];
/**
 * ChatInput component allows users to type messages, format text, and attach files.
 * It can be configured to show/hide specific features
 */
export const ChatInput = ({ 
//showTextEditor = true,
showAttachment = true, enableSubmit = true, placeholder = 'Type a message...', onSubmit, onChange, onAttach, maxRows = 10, disabled = false, acceptTypes, }) => {
    const classes = useStyles();
    const [message, setMessage] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    // Syncfusion editor ref
    //const [isTextEditorOpen, setIsTextEditorOpen] = useState<boolean>(false);
    //const rteRef = useRef<RichTextEditorComponent>(null);
    // line height for use in max height calculations
    const lineHeight = 21;
    // handle input changes
    const handleInputChange = (e) => {
        const value = e.target.value;
        setMessage(value);
        if (onChange) {
            onChange(value);
        }
    };
    // handle rich text editor changes
    /*
    const handleRteChange = () => {
      if (rteRef.current) {
        const value = rteRef.current.getHtml();
        setMessage(value);
        if (onChange) {
          onChange(value);
        }
      }
    };
    */
    // handle key down event for the input
    const handleKeyDown = (e) => {
        if (enableSubmit && e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };
    // handle submission of message
    const handleSubmit = () => {
        if (!enableSubmit || !message.trim())
            return;
        if (onSubmit) {
            onSubmit(message, attachments);
        }
        // reset state after submit
        setMessage('');
        setAttachments([]);
        setIsUploading(false);
        setUploadProgress(0);
        // reset rte if it is open
        /*
        if (isTextEditorOpen && rteRef.current) {
          rteRef.current.value = '';
        }
        */
        // focus the input after submission
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };
    // handle file attachment
    const handleFileAttachment = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };
    // handle file selection
    const handleFileChange = (e) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const fileArray = Array.from(files);
            setAttachments((prev) => [...prev, ...fileArray]);
            setIsUploading(true);
            // simulate upload progress for now
            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                setUploadProgress(progress);
                if (progress >= 100) {
                    clearInterval(interval);
                    setIsUploading(false);
                }
            }, 50);
            if (onAttach) {
                onAttach(fileArray);
            }
            // reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    // toggle text editor
    /*
    const toggleTextEditor = () => {
      setIsTextEditorOpen(!isTextEditorOpen);
    };
  
    // setup toolbar items for the rich text editor
    const toolbarSettings: ToolbarSettingsModel = {
      items: [
        'Bold',
        'Italic',
        'Underline',
        'StrikeThrough',
        '|',
        'Formats',
        'OrderedList',
        'UnorderedList',
        '|',
        'CreateLink',
        'Image',
      ],
    };
    */
    // focus the input when the component mounts
    useEffect(() => {
        if (inputRef.current instanceof HTMLElement) {
            inputRef.current.focus();
        }
    }, []);
    return (_jsx(Box, { className: classes.root, children: _jsx(Paper, { className: classes.inputContainer, elevation: 0, children: _jsxs("div", { className: classes.inputFieldContainer, children: [_jsx(InputBase, { className: classes.input, multiline: true, rows: 1, rowsMax: maxRows, placeholder: placeholder, value: message, onChange: handleInputChange, onKeyDown: handleKeyDown, inputRef: inputRef, inputProps: {
                            'aria-label': 'compose message',
                            style: { maxHeight: `${lineHeight * maxRows}px` },
                        }, disabled: disabled }), attachments.length > 0 && (_jsx(ChatAttachments, { files: attachments, progress: uploadProgress, isUploading: isUploading, onRemove: (index) => {
                            setAttachments(attachments.filter((_, i) => i !== index));
                        } })), _jsxs("div", { className: classes.actionsContainer, children: [showAttachment && (_jsxs(_Fragment, { children: [_jsx("input", { ref: fileInputRef, type: 'file', multiple: true, className: classes.fileInput, onChange: handleFileChange, "aria-label": 'Attach files', accept: acceptTypes ? acceptTypes : allowedTypes.map((type) => `.${type.split('/')[1]}`).join(',') }), _jsx(Tooltip, { title: 'Attach files', children: _jsx(IconButton, { className: classes.toolbarButton, onClick: handleFileAttachment, "aria-label": 'Attach files', disabled: disabled, children: _jsx(AttachFileIcon, {}) }) })] })), enableSubmit && (_jsxs(_Fragment, { children: [_jsx(Divider, { className: classes.divider, orientation: 'vertical' }), _jsx(Tooltip, { title: 'Send message', children: _jsx(IconButton, { className: classes.toolbarButton, onClick: handleSubmit, disabled: !message.trim() || disabled, "aria-label": 'Send message', children: _jsx(SendIcon, {}) }) })] }))] })] }) }) }));
};
export default ChatInput;
